/**
 * Bottom-pinned compose box for the thread view.
 *
 * Layout: auto-growing textarea (max 4 lines) + amount field + send icon.
 * The amount defaults to "0.01" TON per MVP spec; the user can lower it
 * down to 0.001 (1 mTON) but no lower — anything smaller starts looking
 * like dust and our own dust filter will hide it.
 *
 * Behavior:
 *   - Enter (no shift) submits when on a single-line entry.
 *   - Cmd/Ctrl+Enter always submits.
 *   - Disabled while compose hook is `preparing` or `signing`.
 */
import { useEffect, useRef, useState } from "react";
import type { FormEvent, KeyboardEvent } from "react";
import { parseTonInput } from "../lib/format";
import type { SendStatus } from "../hooks/useCompose";
import { SendIcon, SpinnerIcon } from "./Icons";

const DEFAULT_AMOUNT_INPUT = "0.0001";
const MIN_AMOUNT_NANO = 100_000n; // 0.0001 TON

interface Props {
  status: SendStatus;
  onSend: (args: { comment: string; amount_nano: string }) => void;
  errorMessage: string | null;
}

export function ComposeBox({ status, onSend, errorMessage }: Props): JSX.Element {
  const [comment, setComment] = useState("");
  const [amount, setAmount] = useState(DEFAULT_AMOUNT_INPUT);
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);

  // Reset comment after a successful send.
  useEffect(() => {
    if (status === "sent") {
      setComment("");
      // Don't reset the amount; user is likely to send another at the same value.
    }
  }, [status]);

  // Auto-grow the textarea to fit content, capped at ~4 lines.
  useEffect(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${Math.min(el.scrollHeight, 4 * 22 + 16)}px`;
  }, [comment]);

  const busy = status === "preparing" || status === "signing";
  const amountNano = parseTonInput(amount);
  const trimmed = comment.trim();
  const canSend =
    !busy &&
    trimmed.length > 0 &&
    trimmed.length <= 120 &&
    amountNano !== null &&
    BigInt(amountNano) >= MIN_AMOUNT_NANO;

  function handleSubmit(e?: FormEvent): void {
    if (e) e.preventDefault();
    if (!canSend || amountNano === null) return;
    onSend({ comment: trimmed, amount_nano: amountNano });
  }

  function onKeyDown(e: KeyboardEvent<HTMLTextAreaElement>): void {
    if (e.key === "Enter" && !e.shiftKey && (e.metaKey || e.ctrlKey || !comment.includes("\n"))) {
      e.preventDefault();
      handleSubmit();
    }
  }

  const over = trimmed.length > 120;
  const amountInvalid = amount.length > 0 && amountNano === null;

  const PRESETS = ["0.0001", "0.001", "0.01"];

  return (
    <form
      onSubmit={handleSubmit}
      className="safe-bottom border-t border-tg-separator bg-tg-bg px-3 pt-2"
    >
      {/* Amount row: editable input + tappable preset chips */}
      <div className="flex items-center gap-2 px-1 pb-2">
        <span className="text-[11px] text-tg-hint">Send</span>
        <div
          className={`flex items-center gap-1 rounded-full border px-2.5 py-1 ${
            amountInvalid
              ? "border-tg-destructive"
              : "border-tg-separator focus-within:border-tg-button"
          } bg-tg-secondary-bg`}
        >
          <input
            type="text"
            inputMode="decimal"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            disabled={busy}
            aria-label="TON amount"
            className={`w-16 bg-transparent text-right text-[13px] tabular-nums focus:outline-none ${
              amountInvalid ? "text-tg-destructive" : "text-tg-text"
            }`}
          />
          <span
            className={`text-[11px] font-medium ${
              amountInvalid ? "text-tg-destructive" : "text-tg-hint"
            }`}
          >
            TON
          </span>
        </div>
        <div className="flex items-center gap-1">
          {PRESETS.map((p) => (
            <button
              type="button"
              key={p}
              onClick={() => setAmount(p)}
              disabled={busy}
              className={`rounded-full px-2 py-0.5 text-[11px] tabular-nums transition-colors ${
                amount === p
                  ? "bg-tg-button text-tg-button-text"
                  : "bg-tg-secondary-bg text-tg-hint hover:text-tg-text"
              }`}
            >
              {p}
            </button>
          ))}
        </div>
      </div>

      {/* Comment row: textarea + send */}
      <div className="flex items-end gap-2">
        <div className="flex flex-1 items-end gap-2 rounded-2xl bg-tg-secondary-bg px-3 py-2">
          <textarea
            ref={textareaRef}
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder="Message…"
            rows={1}
            disabled={busy}
            aria-label="Message comment"
            className="scroll-area block w-full resize-none bg-transparent text-[15px] leading-snug text-tg-text placeholder:text-tg-hint focus:outline-none disabled:opacity-60"
          />
        </div>
        <button
          type="submit"
          disabled={!canSend}
          aria-label="Send"
          className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-tg-button text-tg-button-text transition-opacity disabled:opacity-40"
        >
          {busy ? <SpinnerIcon size={18} /> : <SendIcon size={18} />}
        </button>
      </div>

      {/* Footer line: char count, errors, dust hint */}
      <div className="flex items-center justify-between px-2 pb-1 pt-1 text-[11px]">
        <span className="text-tg-hint">
          {status === "preparing"
            ? "Preparing transfer…"
            : status === "signing"
              ? "Waiting for wallet — open Tonkeeper to approve."
              : status === "rejected"
                ? "Send canceled in wallet."
                : errorMessage !== null
                  ? errorMessage
                  : "Wallet signs the transfer; we never see your keys."}
        </span>
        <span className={over ? "text-tg-destructive" : "text-tg-hint"}>
          {trimmed.length}/120
        </span>
      </div>
    </form>
  );
}
